<script>
import { createPopper } from "@popperjs/core";

export default {
  name: "SnsPopper",
  props: {
    placement: {
      type: String,
      default: "top",
    },
    showContent: {
      type: Boolean,
      default: true,
    },
    showDelay: {
      type: Number,
      default: 0,
    },
    hideDelay: {
      type: Number,
      default: 0,
    },
    onlyWhenTruncated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      isVisible: false,
      popperInstance: null,
      showTimer: null,
      hideTimer: null,
    };
  },
  computed: {
    popperConfig() {
      return {
        placement: this.placement,
        strategy: "fixed",
        modifiers: [
          {
            name: "offset",
            options: {
              offset: [0, 8],
            },
          },
          {
            name: "preventOverflow",
            options: {
              padding: 8,
              boundary: "viewport",
            },
          },
        ],
      };
    },
  },
  watch: {
    placement() {
      if (this.popperInstance) {
        this.popperInstance.setOptions(this.popperConfig);
        this.popperInstance.update();
      }
    },
    isOpen(newVal) {
      if (newVal) {
        this.isVisible = true;
        if (this.popperInstance) {
          this.popperInstance.update();
        }
      } else {
        this.isVisible = false;
      }
    },
    showContent(newVal) {
      if (newVal) {
        this.$nextTick(() => {
          this.initPopper();
        });
      } else {
        if (this.popperInstance) {
          this.popperInstance.destroy();
          this.popperInstance = null;
        }
      }
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initPopper();
    });
  },
  beforeUnmount() {
    this.clearTimers();
    if (this.popperInstance) {
      this.popperInstance.destroy();
      this.popperInstance = null;
    }
  },
  methods: {
    initPopper() {
      if (!this.popperInstance && this.$refs.element && this.$refs.tooltip) {
        this.popperInstance = createPopper(this.$refs.element, this.$refs.tooltip, this.popperConfig);
      }
    },
    clearTimers() {
      if (this.showTimer) {
        clearTimeout(this.showTimer);
        this.showTimer = null;
      }

      if (this.hideTimer) {
        clearTimeout(this.hideTimer);
        this.hideTimer = null;
      }
    },
    isTruncated() {
      const el = this.$refs.element;
      if (!el) return false;
      const target = el.querySelector(".truncate") || el.firstElementChild || el;
      return target.scrollWidth > target.clientWidth;
    },
    show() {
      this.clearTimers();

      if (this.onlyWhenTruncated && !this.isTruncated()) return;

      if (this.showDelay) {
        this.showTimer = setTimeout(() => {
          this.isOpen = true;
        }, this.showDelay);
      } else {
        this.isOpen = true;
      }
    },
    hide() {
      this.clearTimers();

      if (this.hideDelay) {
        this.hideTimer = setTimeout(() => {
          this.isOpen = false;
        }, this.hideDelay);
      } else {
        this.isOpen = false;
      }
    },
  },
};
</script>

<template>
  <div id="sns-popper" @mouseenter="show" @mouseleave="hide">
    <div ref="element" class="element" aria-describedby="tooltip">
      <slot name="element" />
    </div>

    <Teleport to="body">
      <div
        v-if="showContent"
        ref="tooltip"
        class="sns-popper-tooltip"
        :class="{ 'is-visible': isVisible }"
        role="tooltip"
      >
        <slot name="content" />
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss">
#sns-popper {
  display: inline-block;
  max-width: 100%;
  width: 100%;

  .element {
    max-width: 100%;
    width: 100%;
  }
}

.sns-popper-tooltip {
  @apply bg-base-100 border border-accent;
  @apply inline-block font-bold text-sm rounded-md;
  @apply z-[9999] p-2;

  opacity: 0;
  visibility: hidden;
  pointer-events: none;
  transition:
    opacity 0.15s ease-out,
    visibility 0.15s ease-out;

  &.is-visible {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
  }
}

@keyframes tooltipFadeIn {
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}
</style>
